import {SedestralMachine} from "../../../../../../../sedestral/framework/machine/SedestralMachine";
import {IAgentAvatarEyePoint} from "./types/IAgentAvatarEyePoint";
import {IAgentAvatarEyeRender} from "./types/IAgentAvatarEyeRender";

export class AgentAvatarEyes {

    private static POINTS = 48;
    private static MAX_MORPH_SECONDS = .55;
    private static STIFFNESS = Math.pow(Math.PI * 2 * 7, 2);
    private static DAMPING = 2 * .62 * Math.PI * 2 * 7;

    private static morphing: Set<AgentAvatarEyes> = new Set();
    private static requestFrame = SedestralMachine.requestFrame();
    private static running = false;
    private static lastFrame = 0;

    public points: IAgentAvatarEyePoint[][];
    public centers: number[][];
    public onRender: (eyes: IAgentAvatarEyeRender[]) => void;

    private targets: number[][][];
    private elapsed = 0;

    constructor(onRender: (eyes: IAgentAvatarEyeRender[]) => void) {
        this.onRender = onRender;
    }

    public place(centers: number[][], expression: string): void {
        this.centers = centers;
        this.targets = this.shapeOf(expression);
        this.points = this.targets.map(eye => eye.map(point => ({x: point[0], y: point[1], velocityX: 0, velocityY: 0})));
        AgentAvatarEyes.morphing.delete(this);
        this.render();
    }

    public morph(expression: string): void {
        if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
            this.place(this.centers, expression);
            return;
        }
        this.targets = this.shapeOf(expression);
        this.elapsed = 0;
        AgentAvatarEyes.morphing.add(this);
        AgentAvatarEyes.start();
    }

    public stop(): void {
        AgentAvatarEyes.morphing.delete(this);
    }

    private static start(): void {
        if (this.running) {
            return;
        }
        this.running = true;
        this.lastFrame = performance.now();
        this.requestFrame((now: number) => this.tick(now));
    }

    private static tick(now: number): void {
        let seconds = Math.min(.05, (now - this.lastFrame) / 1000);
        this.lastFrame = now;
        this.morphing.forEach(eyes => {
            if (eyes.step(seconds)) {
                this.morphing.delete(eyes);
            }
        });
        if (this.morphing.size > 0) {
            this.requestFrame((time: number) => this.tick(time));
        } else {
            this.running = false;
        }
    }

    private step(seconds: number): boolean {
        let steps = Math.ceil(seconds / .008);
        let stepSeconds = seconds / steps;
        for (let step = 0; step < steps; step++) {
            this.points.forEach((eye, eyeIndex) => eye.forEach((point, index) => {
                let target = this.targets[eyeIndex][index];
                point.velocityX += (AgentAvatarEyes.STIFFNESS * (target[0] - point.x) - AgentAvatarEyes.DAMPING * point.velocityX) * stepSeconds;
                point.velocityY += (AgentAvatarEyes.STIFFNESS * (target[1] - point.y) - AgentAvatarEyes.DAMPING * point.velocityY) * stepSeconds;
                point.x += point.velocityX * stepSeconds;
                point.y += point.velocityY * stepSeconds;
            }));
        }

        let moving = 0;
        this.points.forEach((eye, eyeIndex) => eye.forEach((point, index) => {
            let target = this.targets[eyeIndex][index];
            moving = Math.max(moving, Math.abs(target[0] - point.x) + Math.abs(target[1] - point.y) + (Math.abs(point.velocityX) + Math.abs(point.velocityY)) * .02);
        }));

        this.elapsed += seconds;
        let settled = moving < .03 || this.elapsed > AgentAvatarEyes.MAX_MORPH_SECONDS;
        if (settled) {
            this.points = this.targets.map(eye => eye.map(point => ({x: point[0], y: point[1], velocityX: 0, velocityY: 0})));
        }
        this.render();
        return settled;
    }

    private render(): void {
        this.onRender(this.points.map(eye => {
            let path = "M";
            let left = Infinity, right = -Infinity, top = Infinity, bottom = -Infinity;
            eye.forEach((point, index) => {
                path += (index ? "L" : "") + point.x.toFixed(1) + " " + point.y.toFixed(1);
                left = Math.min(left, point.x);
                right = Math.max(right, point.x);
                top = Math.min(top, point.y);
                bottom = Math.max(bottom, point.y);
            });
            let width = right - left;
            let height = bottom - top;
            let visible = height >= 8 && width >= 6;
            return {
                path: path + "Z",
                glintX: left + width * .32,
                glintY: (top + bottom) / 2 - height * .26,
                glintRadius: visible ? Math.max(1.3, Math.min(2.8, width * .2)) : 0
            };
        }));
    }

    private shapeOf(expression: string): number[][][] {
        return this.centers.map((center, index) => AgentAvatarEyes.expression(expression, index ? 1 : -1, center[0], center[1]));
    }

    private static expression(name: string, side: number, centerX: number, centerY: number): number[][] {
        switch (name) {
            case "happy":
                return this.crescent(centerX, centerY, 10, 8, 5);
            case "laugh":
                return this.crescent(centerX, centerY, 11, 8.5, 6);
            case "wink":
                return side < 0 ? this.crescent(centerX, centerY, 10, 8, 5) : this.superEllipse(8, 12, 2.8, centerX, centerY);
            case "focus":
                return this.superEllipse(9, 6, 3.2, centerX, centerY + 2);
            case "up":
                return this.superEllipse(7.5, 10.5, 2.8, centerX, centerY - 2);
            case "tall":
                return this.superEllipse(8, 15, 2.6, centerX, centerY - 1);
            case "wide":
                return this.superEllipse(10.5, 14.5, 2.3, centerX, centerY);
            case "tiny":
                return this.superEllipse(4.2, 5.5, 2, centerX, centerY);
            case "closed":
                return this.superEllipse(9, 1.8, 2.4, centerX, centerY + 3);
            case "shy":
                return this.superEllipse(7, 8, 2.6, centerX - 4, centerY + 5);
            case "worried":
                return this.superEllipse(8, 11, 2.8, centerX, centerY).map(([x, y]) => [x, y < centerY ? y - (x - centerX) * side * .45 : y]);
            case "sad":
                return this.superEllipse(7.5, 9.5, 2.8, centerX, centerY + 3).map(([x, y]) => [x, y < centerY + 3 ? y - (x - centerX) * side * .5 : y]);
            case "sleepy":
                return this.superEllipse(8.5, 12, 2.8, centerX, centerY).map(([x, y]) => [x, Math.max(y, centerY + 1)]);
            case "bored":
                return this.superEllipse(8.5, 12, 2.8, centerX + 2, centerY + 1).map(([x, y]) => [x, Math.max(y, centerY + 2)]);
            case "smug":
                return this.superEllipse(8.5, 12, 2.8, centerX, centerY - 1).map(([x, y]) => [x, Math.max(y, centerY - 2)]);
            case "suspicious":
                return this.superEllipse(9.5, 5, 3, centerX, centerY + 1).map(([x, y]) => [x, y < centerY + 1 ? y + (x - centerX) * side * .35 : y]);
            case "angry":
                return this.superEllipse(9, 10, 2.8, centerX, centerY).map(([x, y]) => [x, y < centerY ? Math.max(y, centerY - 10 + (x - centerX) * side * .9) : y]);
            case "curious":
                return side > 0 ? this.superEllipse(10, 14, 2.4, centerX, centerY - 1) : this.superEllipse(7.5, 9, 2.8, centerX, centerY + 1);
            case "confused":
                return side < 0 ? this.superEllipse(9, 5, 3, centerX, centerY + 2) : this.superEllipse(9.5, 13.5, 2.4, centerX, centerY - 2);
            case "dizzy":
                return this.rotate(this.superEllipse(10, 2.4, 2.4, centerX, centerY), centerX, centerY, side * 38);
            case "star":
                return this.star(centerX, centerY);
            case "heart":
                return this.heart(centerX, centerY);
            default:
                return this.superEllipse(8, 12, 2.8, centerX, centerY);
        }
    }

    private static superEllipse(width: number, height: number, exponent: number, centerX: number, centerY: number): number[][] {
        return Array.from({length: this.POINTS}, (_, index) => {
            let angle = index / this.POINTS * Math.PI * 2;
            let cosine = Math.cos(angle);
            let sine = Math.sin(angle);
            return [
                centerX + width * Math.sign(cosine) * Math.pow(Math.abs(cosine), 2 / exponent),
                centerY - height * Math.sign(sine) * Math.pow(Math.abs(sine), 2 / exponent)
            ];
        });
    }

    private static crescent(centerX: number, centerY: number, outer: number, inner: number, offset: number): number[][] {
        let half = this.POINTS / 2;
        let points: number[][] = [];
        for (let index = 0; index < half; index++) {
            let angle = index / (half - 1) * Math.PI;
            points.push([centerX + outer * Math.cos(angle), centerY + 2 - outer * .95 * Math.sin(angle)]);
        }
        for (let index = 0; index < half; index++) {
            let angle = Math.PI - index / (half - 1) * Math.PI;
            points.push([centerX + inner * Math.cos(angle) * .92, centerY + 2 + offset - (inner + offset - 3) * Math.sin(angle) * .9]);
        }
        return points;
    }

    private static rotate(points: number[][], centerX: number, centerY: number, degrees: number): number[][] {
        let angle = degrees * Math.PI / 180;
        let cosine = Math.cos(angle);
        let sine = Math.sin(angle);
        return points.map(([x, y]) => [centerX + (x - centerX) * cosine - (y - centerY) * sine, centerY + (x - centerX) * sine + (y - centerY) * cosine]);
    }

    private static star(centerX: number, centerY: number): number[][] {
        let vertices = Array.from({length: 10}, (_, index) => {
            let angle = -Math.PI / 2 + index * Math.PI / 5;
            let radius = index % 2 ? 5 : 12;
            return [centerX + radius * Math.cos(angle), centerY + 1 + radius * Math.sin(angle)];
        });
        return this.resample(vertices);
    }

    private static heart(centerX: number, centerY: number): number[][] {
        return Array.from({length: this.POINTS}, (_, index) => {
            let angle = index / this.POINTS * Math.PI * 2;
            return [
                centerX + .68 * 16 * Math.pow(Math.sin(angle), 3),
                centerY - .68 * (13 * Math.cos(angle) - 5 * Math.cos(2 * angle) - 2 * Math.cos(3 * angle) - Math.cos(4 * angle)) - 1
            ];
        });
    }

    private static resample(polygon: number[][]): number[][] {
        let lengths = [0];
        for (let index = 1; index <= polygon.length; index++) {
            let from = polygon[index - 1];
            let to = polygon[index % polygon.length];
            lengths.push(lengths[index - 1] + Math.hypot(to[0] - from[0], to[1] - from[1]));
        }
        let total = lengths[polygon.length];
        return Array.from({length: this.POINTS}, (_, index) => {
            let distance = index / this.POINTS * total;
            let segment = 0;
            while (lengths[segment + 1] < distance) {
                segment++;
            }
            let from = polygon[segment];
            let to = polygon[(segment + 1) % polygon.length];
            let ratio = (distance - lengths[segment]) / (lengths[segment + 1] - lengths[segment] || 1);
            return [from[0] + (to[0] - from[0]) * ratio, from[1] + (to[1] - from[1]) * ratio];
        });
    }
}
